import React, { Dispatch, SetStateAction, useState } from 'react';
import slugify from 'slugify';
import { useRouter } from 'next/router';

import Checkbox, { CheckboxInputProps } from 'Elements/Input/Checkbox';

import { DropdownListItem, CheckboxListItem } from './useFilters.styled';

export type Project = 'staff' | 'students' | 'website' | 'warsash' | 'sportscomplex' | 'global';

export interface ActiveFilter {
    id: string;
    type: string;
    label: string;
}

export interface Filter {
    id: string;
    name: string;
    label: string;
    value: string;
}

export interface FilterGroup {
    type: string;
    label: string;
    options: Filter[];
}

export interface UseFilters {
    activeFilters: ActiveFilter[];
    searchTerm: string | undefined;
    activeProject: Project | undefined;
    removeFilter: (id: string) => void;
    filterResults: () => void;
    setSearchTerm: Dispatch<SetStateAction<string | undefined>>;
    setActiveProject: Dispatch<SetStateAction<Project | undefined>>;
    generateDropdownOptions: (filter: FilterGroup) => React.JSX.Element[];
    generateCheckboxOptions: (filter: CheckboxInputProps[], type: string) => React.JSX.Element[];
    resetActiveFilters: () => void;
}

const useFilters = (
    initialActiveFilters: ActiveFilter[],
    initialSearchTerm?: string,
    project?: Project
): UseFilters => {
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialActiveFilters);
    const [searchTerm, setSearchTerm] = useState<string | undefined>(initialSearchTerm);
    const [activeProject, setActiveProject] = useState<Project | undefined>(project);

    const router = useRouter();
    const pathname = router.pathname;

    const addFilter = (id: string, type: string, label: string) => {
        setActiveFilters([...activeFilters, { id, type, label }]);
    };

    const removeFilter = (id: string) => {
        const findAndRemoveFilter = (filters: ActiveFilter[]): ActiveFilter[] => {
            const updatedFilters: ActiveFilter[] = [];

            for (const filter of filters) {
                if (filter.id === id) {
                    continue;
                } else {
                    updatedFilters.push(filter);
                }
            }

            return updatedFilters;
        };

        setActiveFilters(findAndRemoveFilter(activeFilters));
    };

    const handleFilter = (activeFilter: boolean, id: string, type: string, label: string) => {
        if (activeFilter) {
            removeFilter(id);
        } else {
            addFilter(id, type, label);
        }
    };

    const filterResults = () => {
        const query = new URLSearchParams();
        activeProject && query.set('project', activeProject);

        activeFilters.forEach(filter => {
            if (query.has(filter.type)) {
                query.set(slugify(filter.type), `${query.get(filter.type)},${filter.id}`);
            } else {
                query.append(slugify(filter.type), filter.id);
            }
        });

        if (searchTerm) {
            query.set('searchTerm', searchTerm);
        }

        query.set('page', '1');

        const qs = query.toString();
        const newUrl = `${pathname}?${qs}`;

        // Push the new URL with qs
        router.push(newUrl, undefined, { scroll: false });
    };

    const generateDropdownOptions = (filter: FilterGroup) =>
        filter.options.map(item => {
            const activeFilter = activeFilters.find(({ id }) => id === item.id);
            return (
                <React.Fragment key={`filter-${item.id}`}>
                    <DropdownListItem
                        role="option"
                        aria-selected={activeFilter ? true : false}
                        tabIndex={0}
                        onClick={() =>
                            handleFilter(
                                activeFilter ? true : false,
                                item.id,
                                filter.type,
                                item.label
                            )
                        }
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleFilter(!!activeFilter, item.id, filter.type, item.label);
                            }
                        }}
                        $isActive={!!activeFilter}
                        {...item}
                    >
                        {item.label}
                    </DropdownListItem>
                </React.Fragment>
            );
        });

    const generateCheckboxOptions = (filter: CheckboxInputProps[], type: string) =>
        filter.map(item => {
            const activeFilter = activeFilters.find(({ id }) => id === item.id);
            return (
                <CheckboxListItem key={item.id}>
                    <Checkbox
                        {...item}
                        defaultChecked={activeFilter ? true : false}
                        onChange={() =>
                            handleFilter(activeFilter ? true : false, item.id, type, item.label)
                        }
                    />
                </CheckboxListItem>
            );
        });

    const resetActiveFilters = () => {
        setActiveFilters([]);
    };

    return {
        activeFilters,
        searchTerm,
        activeProject,
        removeFilter,
        filterResults,
        setSearchTerm,
        setActiveProject,
        generateDropdownOptions,
        generateCheckboxOptions,
        resetActiveFilters
    };
};

export default useFilters;
